import { useParams } from "react-router-dom";
import FadeInAnimation from "../../components/FadeInAnimation";
import Slider from "rc-slider";
import {
  FaPlay,
  FaPause,
  FaVolumeUp,
  FaVolumeMute,
  FaStepBackward,
  FaStepForward,
  FaTimes,
} from "react-icons/fa";
import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import cx from "classnames";
import { isMobileSafari } from "react-device-detect";
import PlayerBackground from "../../components/PlayerBackground";
import { preloadStationBackground } from "../../services/audioServices";
import ScrollText from "../../components/ScrollText";
import CastButton from "../../components/CastButton";

import "rc-slider/assets/index.css";
import "./PlayerV4.css";

// Importar funciones centralizadas
import {
  isNewSong,
  getCover,
  shareServices,
  updateMediaSession,
  toUpperCase,
  startHistoryUpdater,
  stopHistoryUpdater,
} from "../../services/audioServices";

function PlayerV4() {
  // Obtener el puerto y el nombre desde los parámetros de la URL
  const { port, name } = useParams();

  // Precargar inmediatamente la imagen de fondo
  useEffect(() => {
    if (name) {
      // Precargar la imagen de fondo inmediatamente al montar el componente
      preloadStationBackground(name);
    }
  }, [name]);

  // Función para capitalizar la primera letra
  const capitalize = (str) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Aplicamos capitalización al nombre de la estación
  const displayName = capitalize(name);

  let logoUrl = `https://audio.streaminghd.cl:2020/pub/${name}/background.jpg`;
  let defaultLogoUrl = `https://vod.streaminghd.cl/img/default.jpg`;

  // Estilo para ocultar el botón del sidebar cuando no hay historial
  const hiddenStyle = {
    display: "none",
  };

  // Estado para el reproductor
  const [audio, setAudio] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [shareBoxOpen, setShareBoxOpen] = useState(false);
  const [logoPushed, setLogoPushed] = useState(false);
  const [shareBoxClosing, setShareBoxClosing] = useState(false);
  const [showVolume, setShowVolume] = useState(false);
  const [volume, setVolume] = useState(1);
  const [mute, setMute] = useState(false);
  const [logo, setLogo] = useState(logoUrl);
  const [currentLogo, setCurrentLogo] = useState(logoUrl);
  const [fadeOut, setFadeOut] = useState(false);
  const [song, setSong] = useState("");
  const [singer, setSinger] = useState("");
  const [originalSong, setOriginalSong] = useState("");
  const [originalSinger, setOriginalSinger] = useState("");
  // Estado para el historial de canciones y el sidebar
  const [previousSongs, setPreviousSongs] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isVolumeBarActive, setIsVolumeBarActive] = useState(false);
  // Añadir un timer para controlar el retraso en ocultar la barra de volumen
  const volumeHideTimerRef = useRef(null);
  const volumeBarRef = useRef(null);

  // Referencia para el timer de la animación de cierre
  const shareTimerRef = useRef(null);

  // Referencia para el intervalo de actualización del historial
  const historyUpdaterRef = useRef(null);

  // Efecto para actualizar el título de la página cuando cambia la canción
  useEffect(() => {
    if (originalSong && originalSinger) {
      // Actualizar el título de la página con la canción y el artista
      document.title = `${originalSong} - ${originalSinger} | ${displayName}`;
    } else if (originalSong) {
      // Si solo tenemos la canción
      document.title = `${originalSong} | ${displayName}`;
    } else {
      // Si no hay información de la canción, mostrar solo el nombre de la estación
      document.title = `(Live) ${toUpperCase(displayName)}`;
    }
  }, [originalSong, originalSinger, displayName]);

  // Handlers de audio con referencia estable usando useMemo
  const audioEventHandlers = useMemo(
    () => ({
      handlePlay: () => setIsPlaying(true),
      handlePause: () => setIsPlaying(false),
      handleEnded: () => setIsPlaying(false),
    }),
    []
  );

  // Manejadores de eventos agrupados para compartir
  const shareEventHandlers = {
    emailShare: () => {
      shareServices.emailShare(port, name, originalSong, originalSinger);
    },
    twitterShare: () => {
      shareServices.twitterShare(port, name, originalSong, originalSinger);
    },
    facebookShare: () => {
      shareServices.facebookShare(port, name, originalSong, originalSinger);
    },
    telegramShare: () => {
      shareServices.telegramShare(port, name, originalSong, originalSinger);
    },
    whatsappShare: () => {
      shareServices.whatsappShare(port, name, originalSong, originalSinger);
    },
  };

  // Efecto para manejar el cambio de logo con fade
  useEffect(() => {
    if (logo !== currentLogo) {
      // Iniciar fade out
      setFadeOut(true);

      // Después de un breve retraso, actualizar la imagen y hacer fade in
      const timer = setTimeout(() => {
        setCurrentLogo(logo);
        setFadeOut(false);
      }, 300); // 300ms para el fade out

      return () => clearTimeout(timer);
    }
  }, [logo, currentLogo]);

  // Inicializar el audio
  useEffect(() => {
    // Configuración inicial del reproductor
    setIsPlaying(false);

    // Limpieza al desmontar
    return () => {
      setAudio((currentAudio) => {
        if (currentAudio) {
          try {
            // Detener reproducción
            currentAudio.pause();

            // Quitar eventos
            currentAudio.removeEventListener(
              "play",
              audioEventHandlers.handlePlay
            );
            currentAudio.removeEventListener(
              "pause",
              audioEventHandlers.handlePause
            );
            currentAudio.removeEventListener(
              "ended",
              audioEventHandlers.handleEnded
            );

            // Limpiar fuentes
            currentAudio.src = "";
            currentAudio.load();

            // Llamar a cleanup si existe
            if (
              currentAudio.cleanup &&
              typeof currentAudio.cleanup === "function"
            ) {
              currentAudio.cleanup();
            }
          } catch (error) {
            console.error("Error al limpiar audio:", error);
          }
        }
        return null;
      });
    };
  }, [name, port, audioEventHandlers]);

  // Función para obtener la canción actual
  const getCurrentSong = useCallback(() => {
    let isMounted = true; // Para evitar actualizar estado en componentes desmontados

    isNewSong(port, singer, song)
      .then((response) => {
        if (!isMounted) return;

        getCover({
          singer: response.singer,
          song: response.song,
        })
          .then((cover) => {
            if (!isMounted) return;

            // Guardar versiones temporales para actualizar después
            const newSong = response.song;
            const newSinger = response.singer;
            const newOriginalSong = response.originalSong || response.song;
            const newOriginalSinger =
              response.originalSinger || response.singer;

            // Retraso de 2 segundos antes de cambiar toda la información
            setTimeout(() => {
              if (!isMounted) return;

              // Guardar versiones limpias para búsqueda futura
              setSong(newSong);
              setSinger(newSinger);

              // Guardar versiones originales con paréntesis
              setOriginalSong(newOriginalSong);
              setOriginalSinger(newOriginalSinger);

              // Actualizar el cover
              setLogo(cover);

              // Actualizar MediaSession para iOS
              updateMediaSession(
                newOriginalSong, // título
                newOriginalSinger, // artista
                displayName, // álbum (nombre de la estación)
                cover // portada
              );
            }, 2000);

            // Programar la siguiente actualización directamente después de actualizar la carátula
            setTimeout(getCurrentSong, 10000);
          })
          .catch(() => {
            // Si falla la portada, al menos actualizar la canción con el mismo retraso
            if (!isMounted) return;

            // Guardar versiones temporales
            const newSong = response.song;
            const newSinger = response.singer;
            const newOriginalSong = response.originalSong || response.song;
            const newOriginalSinger =
              response.originalSinger || response.singer;

            // Aplicar el mismo retraso de 2 segundos
            setTimeout(() => {
              if (!isMounted) return;

              setSong(newSong);
              setSinger(newSinger);
              setOriginalSong(newOriginalSong);
              setOriginalSinger(newOriginalSinger);
            }, 2000);

            // Programar la siguiente actualización incluso si falló la obtención de carátula
            setTimeout(getCurrentSong, 10000);
          });
      })
      .catch(() => {
        if (isMounted) {
          // Reintento después de un tiempo incluso si hay error
          setTimeout(getCurrentSong, 10000);
        }
      });

    // Función de limpieza para evitar memory leaks
    return () => {
      isMounted = false;
    };
  }, [port, singer, song, displayName]);

  // Obtener la canción actual al cargar el componente
  useEffect(() => {
    if (port) {
      getCurrentSong();

      // Iniciar la actualización automática del historial
      historyUpdaterRef.current = startHistoryUpdater(port, setPreviousSongs);

      // Limpiar el intervalo cuando el componente se desmonte
      return () => {
        stopHistoryUpdater(historyUpdaterRef.current);
      };
    }
  }, [port, getCurrentSong]);

  // Reproducir audio cuando se actualiza
  useEffect(() => {
    if (audio && port) {
      // No reproducimos automáticamente, solo configuramos el volumen
      audio.volume = volume;
    }
  }, [port, audio, volume]);

  // Determinar si el dispositivo tiene pantalla táctil
  const isTouchScreenDevice = () => {
    return (
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0 ||
      navigator.msMaxTouchPoints > 0
    );
  };

  // Referencia para los controles
  const controlsSectionRef = useRef(null);
  const nextButtonRef = useRef(null);
  const shareButtonRef = useRef(null);

  // Efecto para precargar elementos visuales críticos y garantizar renderizado correcto
  useEffect(() => {
    // Asegurar que los componentes visuales estén listos antes de mostrarlos
    document.documentElement.style.setProperty("--player-ready", "1");

    // Precargar íconos clave (solo para evitar warnings de linter)
    void [
      FaPlay,
      FaPause,
      FaVolumeUp,
      FaVolumeMute,
      FaStepBackward,
      FaStepForward,
    ];

    // Forzar reflow para asegurar que los botones se rendericen correctamente
    if (controlsSectionRef.current) {
      // Forzar un repintado para asegurar la posición correcta
      controlsSectionRef.current.style.display = "flex";

      // Forzar un foco especial en los botones de la derecha que han tenido problemas
      if (nextButtonRef.current) {
        nextButtonRef.current.style.transform = "translateZ(0)";
        nextButtonRef.current.style.position = "relative";
      }

      if (shareButtonRef.current) {
        shareButtonRef.current.style.transform = "translateZ(0)";
        shareButtonRef.current.style.position = "relative";
      }
    }

    return () => {
      document.documentElement.style.setProperty("--player-ready", "0");
    };
  }, []);

  // Efecto para cerrar el control de volumen al tocar fuera de él en dispositivos móviles
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        isVolumeBarActive &&
        volumeBarRef.current &&
        !volumeBarRef.current.contains(event.target) &&
        event.target.className !== "V4VolumeButton" &&
        !event.target.closest(".V4VolumeButton")
      ) {
        setIsVolumeBarActive(false);
      }
    };

    if (isVolumeBarActive) {
      document.addEventListener("touchstart", handleOutsideClick);
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("touchstart", handleOutsideClick);
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isVolumeBarActive]);

  // Efecto para limpiar el timer de la barra de volumen al desmontar
  useEffect(() => {
    return () => {
      if (volumeHideTimerRef.current) {
        clearTimeout(volumeHideTimerRef.current);
        volumeHideTimerRef.current = null;
      }
    };
  }, []);

  // Referencia para guardar el último volumen antes de mutear
  const lastVolumeRef = useRef(1);

  // Efecto para actualizar el volumen del audio cuando cambia
  useEffect(() => {
    if (audio) {
      audio.volume = volume;
    }
    // Guardar el último volumen no-cero para restaurar al desmutear
    if (volume > 0) {
      lastVolumeRef.current = volume;
    }
    // Actualizar estado de mute basado en volumen
    if (volume === 0) {
      setMute(true);
    } else {
      setMute(false);
    }
  }, [volume, audio]);

  // Efecto para sincronizar el estado mute con la propiedad muted del audio
  // Específicamente útil para dispositivos iOS Safari
  useEffect(() => {
    if (audio) {
      audio.muted = mute;

      // Para iOS Safari, necesitamos asegurarnos que el volumen también se ajusta
      if (isMobileSafari) {
        // Cuando está muteado en iOS, también establecemos el volumen a 0 como respaldo
        if (mute) {
          audio.volume = 0;
        } else {
          audio.volume = lastVolumeRef.current;
        }
      }
    }
  }, [mute, audio]);

  // Control de reproducción - limpio y optimizado
  const onTogglePlay = () => {
    if (isPlaying) {
      // Si está reproduciendo, pausar
      if (audio) {
        try {
          audio.pause();
        } catch (error) {
          console.error("Error al pausar el audio:", error);
        }
      }
      setIsPlaying(false);

      // Actualizar estado de MediaSession
      if ("mediaSession" in navigator) {
        navigator.mediaSession.playbackState = "paused";
      }
    } else {
      // Crear una nueva conexión de audio para reproducir
      createNewAudioConnection();

      // Actualizar estado de MediaSession y metadatos
      if ("mediaSession" in navigator) {
        navigator.mediaSession.playbackState = "playing";

        // Actualizar metadatos con la información actual
        updateMediaSession(originalSong, originalSinger, displayName, logo);
      }
    }
  };

  // Función para crear una nueva conexión de audio
  const createNewAudioConnection = () => {
    setIsPlaying(true);

    // URL principal que funciona correctamente
    const streamUrl = `https://audio.streaminghd.cl:2000/stream/${name}/;stream.mp3`;
    const newAudio = new Audio(streamUrl);

    // Aplicar el volumen actual al nuevo elemento de audio
    newAudio.volume = volume;

    // En iOS Safari, aseguramos que el estado mute se aplique correctamente
    if (isMobileSafari && mute) {
      newAudio.muted = true;
    }

    newAudio.play().catch(() => {
      // Si falla, intentar URL alternativa
      const alternativeUrl = `http://audio.streaminghd.cl:${port}/;stream.mp3`;
      const alternativeAudio = new Audio(alternativeUrl);
      alternativeAudio.volume = volume;

      // En iOS Safari, aseguramos que el estado mute se aplique correctamente
      if (isMobileSafari && mute) {
        alternativeAudio.muted = true;
      }

      alternativeAudio.play().catch(() => {
        setIsPlaying(false);
      });

      setAudio(alternativeAudio);
    });

    // Establecer el nuevo audio en el estado
    setAudio(newAudio);

    // Actualizar estado de MediaSession
    if ("mediaSession" in navigator) {
      navigator.mediaSession.playbackState = "playing";
    }
  };

  // Manejar apertura y cierre del cuadro de compartir con animación
  const handleShareToggle = () => {
    if (shareBoxOpen) {
      // Iniciar animación de cierre
      setShareBoxClosing(true);
      // Cerrar el cuadro después de que termine la animación
      const timer = setTimeout(() => {
        setShareBoxOpen(false);
        setShareBoxClosing(false);
      }, 300); // Igual a la duración de la animación

      // Almacenar el timer en una ref para limpiarlo si es necesario
      shareTimerRef.current = timer;
    } else {
      setShareBoxOpen(true);
    }
  };

  // Limpia los timers cuando el componente se desmonta
  useEffect(() => {
    return () => {
      if (shareTimerRef.current) {
        clearTimeout(shareTimerRef.current);
      }
    };
  }, []);

  // Renderizado del componente
  return (
    <div
      className={`PlayerV4Container ${shareBoxOpen ? "v4-sharebox-open" : ""}`}
    >
      {/* Historial de canciones (sidebar) */}
      <div className={cx("History", sidebarOpen ? "active" : "")}>
        {previousSongs.map((previousSong, index) => (
          <div className="HistoryItem" key={index.toString()}>
            <div className="Thumbnail">
              <img
                src={previousSong.cover ? previousSong.cover : logoUrl}
                alt={`${previousSong.song} - ${previousSong.singer}`}
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null;
                  currentTarget.src = defaultLogoUrl;
                }}
              />
            </div>
            <div className="Description">
              <div className="Title">
                <ScrollText
                  text={previousSong.song ? previousSong.song : displayName}
                  elementType="div"
                />
              </div>
              <div className="Author">
                <ScrollText text={previousSong.singer} elementType="div" />
              </div>
              <div className="Time">a las {previousSong.time}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Contenido principal del reproductor */}
      <div className={cx("Home", sidebarOpen ? "" : "active")}>
        {/* Reemplazando imagen con el componente centralizado */}
        <PlayerBackground
          backgroundImage={currentLogo ? currentLogo : logoUrl}
          fallbackImage={defaultLogoUrl}
          type="v4"
          fadeOut={fadeOut}
        />

        {/* Botón para abrir/cerrar el sidebar */}
        <div
          className={cx("SidebarClose", sidebarOpen ? "active" : "")}
          style={previousSongs.length === 0 ? hiddenStyle : {}}
        >
          <div onClick={() => setSidebarOpen(!sidebarOpen)}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>

        <div className="PlayerV4">
          {/* Fondo con blur */}
          <div
            className="V4Background"
            style={{
              backgroundImage: `url(${currentLogo ? currentLogo : logoUrl})`,
              opacity: fadeOut ? 0 : 1, // Controlar la opacidad para el fade
            }}
          />

          {/* Contenido del reproductor */}
          <div className="V4Content">
            {/* Sección superior: Logo e información */}
            <div className="V4TopSection">
              {/* Logo (izquierda) */}
              <FadeInAnimation>
                <div
                  className={`V4Logo ${logoPushed ? "push" : ""}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleShareToggle();
                  }}
                  onMouseDown={() => setLogoPushed(true)}
                  onMouseUp={() => setLogoPushed(false)}
                  onMouseLeave={() => setLogoPushed(false)}
                  onTouchStart={() => setLogoPushed(true)}
                  onTouchEnd={() => setLogoPushed(false)}
                  style={{ cursor: "pointer" }}
                >
                  <img
                    src={logo ? logo : logoUrl}
                    alt={displayName}
                    className="V4LogoImage"
                    onError={({ currentTarget }) => {
                      currentTarget.onerror = null;
                      // Si falla, usar la imagen por defecto
                      currentTarget.src = defaultLogoUrl;
                    }}
                  />
                </div>
              </FadeInAnimation>

              {/* Información (centro) */}
              <FadeInAnimation>
                <div className="V4InfoSection">
                  {/* Indicador "al aire" */}
                  <div className="V4AirStatus">
                    <div className="V4Bars">
                      <div
                        className={`V4Bar ${!isPlaying ? "inactive" : ""}`}
                      ></div>
                      <div
                        className={`V4Bar ${!isPlaying ? "inactive" : ""}`}
                      ></div>
                      <div
                        className={`V4Bar ${!isPlaying ? "inactive" : ""}`}
                      ></div>
                    </div>
                    <div className="V4AirLabel">AL AIRE</div>
                  </div>

                  {/* Título y descripción */}
                  <div className="V4Title">
                    <ScrollText
                      text={
                        originalSong ? originalSong : toUpperCase(displayName)
                      }
                      elementType="h1"
                    />
                  </div>
                  <div className="V4Description">
                    <ScrollText text={originalSinger} elementType="p" />
                  </div>
                </div>
              </FadeInAnimation>
            </div>

            {/* Sección inferior: Controles de reproducción */}
            <FadeInAnimation>
              <div className="V4ControlsSection" ref={controlsSectionRef}>
                {/* Control de volumen - EXACTAMENTE IGUAL QUE EN PLAYER.JS */}
                {isMobileSafari ? (
                  <div className="V4VolumeControl">
                    <div
                      className={`V4VolumeButton ${mute ? "muted" : ""}`}
                      onClick={() => {
                        // Toggle mute/unmute
                        const newMuteState = !mute;
                        setMute(newMuteState);

                        // Obtener el nuevo volumen basado en el estado de mute
                        const newVolume = newMuteState
                          ? 0
                          : lastVolumeRef.current;

                        // Actualizar el volumen en el estado
                        setVolume(newVolume);

                        // Actualizar directamente el volumen del elemento audio
                        if (audio) {
                          audio.volume = newVolume;

                          // En iOS Safari, necesitamos una interacción directa con el audio
                          if (isMobileSafari) {
                            // Asegurarnos que el cambio de volumen sea inmediato
                            audio.muted = newMuteState;
                          }
                        }
                      }}
                    >
                      {!mute ? <FaVolumeUp /> : <FaVolumeMute />}
                    </div>
                  </div>
                ) : (
                  <div
                    className={cx("V4VolumeControl", {
                      active: isVolumeBarActive,
                    })}
                    onMouseEnter={() => {
                      if (!isTouchScreenDevice()) {
                        // Limpiar cualquier timer pendiente al entrar al área
                        if (volumeHideTimerRef.current) {
                          clearTimeout(volumeHideTimerRef.current);
                          volumeHideTimerRef.current = null;
                        }
                        setShowVolume(true);
                      }
                    }}
                    onMouseLeave={() => {
                      if (!isTouchScreenDevice()) {
                        // Configurar un timer para ocultar la barra después de un retraso
                        volumeHideTimerRef.current = setTimeout(() => {
                          if (!isVolumeBarActive) {
                            setShowVolume(false);
                          }
                        }, 800); // 800ms de retraso antes de ocultar
                      }
                    }}
                  >
                    <div
                      className={cx("V4VolumeBar", {
                        disabled: !showVolume && !isVolumeBarActive,
                        active: isVolumeBarActive,
                      })}
                      ref={volumeBarRef}
                      onMouseEnter={() => {
                        // Cancelar el timer si el mouse entra a la barra
                        if (volumeHideTimerRef.current) {
                          clearTimeout(volumeHideTimerRef.current);
                          volumeHideTimerRef.current = null;
                        }
                      }}
                    >
                      <div className="V4VolumeBarContainer">
                        <Slider
                          vertical
                          min={0}
                          max={1}
                          value={volume}
                          step={0.01}
                          onChange={(value) => setVolume(value)}
                          handleStyle={{
                            borderColor: "#ffffff",
                            backgroundColor: "#f62b4a",
                            height: 16,
                            width: 16,
                            marginLeft: -6,
                            marginTop: -8,
                            boxShadow: "0 2px 5px rgba(0, 0, 0, 0.5)",
                            opacity: 1,
                          }}
                          railStyle={{
                            backgroundColor: "rgba(255, 255, 255, 0.2)",
                            width: 4,
                            borderRadius: 2,
                          }}
                          trackStyle={{
                            backgroundColor: "#f62b4a",
                            width: 4,
                            borderRadius: 2,
                          }}
                        />
                      </div>
                    </div>
                    <div
                      className="V4VolumeButton"
                      onClick={() => {
                        if (isTouchScreenDevice() || isMobileSafari) {
                          // En dispositivos móviles, simplemente alternar entre mute/unmute
                          setMute(!mute);
                          setVolume(mute ? lastVolumeRef.current : 0);
                        } else {
                          // En desktop, comportamiento normal
                          if (isTouchScreenDevice()) {
                            setIsVolumeBarActive(!isVolumeBarActive);
                          } else {
                            setMute(!mute);
                            setVolume(mute ? lastVolumeRef.current : 0);
                          }
                        }
                      }}
                    >
                      {!mute ? <FaVolumeUp /> : <FaVolumeMute />}
                    </div>
                  </div>
                )}

                {/* Botón anterior */}
                <div className="V4PreviousControl">
                  <div className="V4PreviousButton">
                    <FaStepBackward />
                  </div>
                </div>

                {/* Botón reproducir/pausar */}
                <div
                  className="V4PlayControl"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onTogglePlay();
                  }}
                >
                  <div className="V4PlayButton">
                    {!isPlaying ? (
                      <FaPlay color="#FFFFFF" />
                    ) : (
                      <FaPause color="#FFFFFF" />
                    )}
                  </div>
                </div>

                {/* Botón siguiente */}
                <div className="V4NextControl">
                  <div className="V4NextButton" ref={nextButtonRef}>
                    <FaStepForward />
                  </div>
                </div>

                {/* Botón Cast en reemplazo del botón Share original */}
                <div
                  className={`V4ShareControl ${shareBoxOpen ? "active" : ""}`}
                >
                  <CastButton
                    ref={shareButtonRef}
                    className="V4ShareButton"
                    streamUrl={`https://audio.streaminghd.cl:2000/stream/${name}/;stream.mp3`}
                    image={logoUrl}
                    audio={audio}
                    title={toUpperCase(displayName)}
                    artist=""
                    album={toUpperCase(displayName)}
                  />
                </div>
              </div>
            </FadeInAnimation>
          </div>
        </div>
      </div>

      {/* ShareBox renderizado fuera de la estructura anidada para un correcto posicionamiento */}
      {shareBoxOpen && (
        <div className={`V4ShareBox ${shareBoxClosing ? "closing" : ""}`}>
          <div className="V4CloseButton" onClick={handleShareToggle}>
            <FaTimes color="#FFFFFF" />
          </div>
          <div className="V4ShareTitle">Compartir Radio</div>
          <div className="V4ShareCover">
            <img
              alt={name}
              src={logo}
              onError={({ currentTarget }) => {
                currentTarget.onerror = null;
                currentTarget.src = defaultLogoUrl;
              }}
            />
          </div>
          <div className="V4ShareTrackInfo">
            <div className="V4SongName">{originalSong || displayName}</div>
            <div className="V4ArtistName">
              {originalSinger || (originalSong ? "" : "Radio Online")}
            </div>
          </div>
          <div className="V4ShareButonActions">
            <button
              aria-label="email"
              className="react-share__ShareButton"
              onClick={shareEventHandlers.emailShare}
              style={{
                backgroundColor: "transparent",
                border: "none",
                padding: 0,
                font: "inherit",
                color: "inherit",
                cursor: "pointer",
              }}
            >
              <svg viewBox="0 0 64 64" width="32" height="32">
                <circle cx="32" cy="32" r="31" fill="#7f7f7f"></circle>
                <path
                  d="M17,22v20h30V22H17z M41.1,25L32,32.1L22.9,25H41.1z M20,39V26.6l12,9.3l12-9.3V39H20z"
                  fill="white"
                ></path>
              </svg>
            </button>
            <button
              aria-label="twitter"
              className="react-share__ShareButton"
              onClick={shareEventHandlers.twitterShare}
              style={{
                backgroundColor: "transparent",
                border: "none",
                padding: 0,
                font: "inherit",
                color: "inherit",
                cursor: "pointer",
              }}
            >
              <svg viewBox="0 0 64 64" width="32" height="32">
                <circle cx="32" cy="32" r="31" fill="#00aced"></circle>
                <path
                  d="M48,22.1c-1.2,0.5-2.4,0.9-3.8,1c1.4-0.8,2.4-2.1,2.9-3.6c-1.3,0.8-2.7,1.3-4.2,1.6 C41.7,19.8,40,19,38.2,19c-3.6,0-6.6,2.9-6.6,6.6c0,0.5,0.1,1,0.2,1.5c-5.5-0.3-10.3-2.9-13.5-6.9c-0.6,1-0.9,2.1-0.9,3.3 c0,2.3,1.2,4.3,2.9,5.5c-1.1,0-2.1-0.3-3-0.8c0,0,0,0.1,0,0.1c0,3.2,2.3,5.8,5.3,6.4c-0.6,0.1-1.1,0.2-1.7,0.2c-0.4,0-0.8,0-1.2-0.1 c0.8,2.6,3.3,4.5,6.1,4.6c-2.2,1.8-5.1,2.8-8.2,2.8c-0.5,0-1.1,0-1.6-0.1c2.9,1.9,6.4,2.9,10.1,2.9c12.1,0,18.7-10,18.7-18.7 c0-0.3,0-0.6,0-0.8C46,24.5,47.1,23.4,48,22.1z"
                  fill="white"
                ></path>
              </svg>
            </button>
            <button
              aria-label="facebook"
              className="react-share__ShareButton"
              onClick={shareEventHandlers.facebookShare}
              style={{
                backgroundColor: "transparent",
                border: "none",
                padding: 0,
                font: "inherit",
                color: "inherit",
                cursor: "pointer",
              }}
            >
              <svg viewBox="0 0 64 64" width="32" height="32">
                <circle cx="32" cy="32" r="31" fill="#3b5998"></circle>
                <path
                  d="M34.1,47V33.3h4.6l0.7-5.3h-5.3v-3.4c0-1.5,0.4-2.6,2.6-2.6l2.8,0v-4.8c-0.5-0.1-2.2-0.2-4.1-0.2 c-4.1,0-6.9,2.5-6.9,7V28H24v5.3h4.6V47H34.1z"
                  fill="white"
                ></path>
              </svg>
            </button>
            <button
              aria-label="telegram"
              className="react-share__ShareButton"
              onClick={shareEventHandlers.telegramShare}
              style={{
                backgroundColor: "transparent",
                border: "none",
                padding: 0,
                font: "inherit",
                color: "inherit",
                cursor: "pointer",
              }}
            >
              <svg viewBox="0 0 64 64" width="32" height="32">
                <circle cx="32" cy="32" r="31" fill="#37aee2"></circle>
                <path
                  d="m45.90873,15.44335c-0.6901,-0.0281 -1.37668,0.14048 -1.96142,0.41265c-0.84989,0.32661 -8.63939,3.33986 -16.5237,6.39174c-3.9685,1.53296 -7.93349,3.06593 -10.98537,4.24067c-3.05012,1.1765 -5.34694,2.05098 -5.4681,2.09312c-0.80775,0.28096 -1.89996,0.63566 -2.82712,1.72788c-0.23354,0.27218 -0.46884,0.62161 -0.58825,1.10275c-0.11941,0.48114 -0.06673,1.09222 0.16682,1.5716c0.46533,0.96052 1.25376,1.35737 2.18443,1.71383c3.09051,0.99037 6.28638,1.93508 8.93263,2.8236c0.97632,3.44171 1.91401,6.89571 2.84116,10.34268c0.30554,0.69185 0.97105,0.94823 1.65764,0.95525l-0.00351,0.03512c0,0 0.53908,0.05268 1.06412,-0.07375c0.52679,-0.12292 1.18879,-0.42846 1.79109,-0.99212c0.662,-0.62161 2.45836,-2.38812 3.47683,-3.38552l7.6736,5.66477l0.06146,0.03512c0,0 0.84989,0.59703 2.09312,0.68132c0.62161,0.04214 1.4399,-0.07726 2.14229,-0.59176c0.70766,-0.51626 1.1765,-1.34683 1.396,-2.29506c0.65673,-2.86224 5.00979,-23.57745 5.75257,-27.00686l-0.02107,0.08077c0.51977,-1.93157 0.32837,-3.70159 -0.87096,-4.74991c-0.60054,-0.52152 -1.2924,-0.7498 -1.98425,-0.77965l0,0.00176zm-0.2072,3.29069c0.04741,0.0439 0.0439,0.0439 0.00351,0.04741c-0.01229,-0.00351 0.14048,0.2072 -0.15804,1.32576l-0.01229,0.04214l-0.00878,0.03863c-0.75858,3.50668 -5.15554,24.40802 -5.74203,26.96472c-0.08077,0.34417 -0.11414,0.31959 -0.09482,0.29852c-0.1756,-0.02634 -0.50045,-0.16506 -0.52679,-0.1756l-13.13468,-9.70175c4.4988,-4.33199 9.09945,-8.25307 13.744,-12.43229c0.8218,-0.41265 0.68483,-1.68573 -0.29852,-1.70681c-1.04305,0.24584 -1.92279,0.99564 -2.8798,1.47502c-5.49971,3.2626 -11.11882,6.13186 -16.55882,9.49279c-2.792,-0.97105 -5.57873,-1.77704 -8.15298,-2.57601c2.2336,-0.89555 4.00889,-1.55579 5.75608,-2.23009c3.05188,-1.1765 7.01687,-2.7042 10.98537,-4.24067c7.94051,-3.06944 15.92667,-6.16346 16.62028,-6.43037l0.05619,-0.02283l0.05268,-0.02283c0.19316,-0.0878 0.30378,-0.09658 0.35471,-0.10009c0,0 -0.01756,-0.05795 -0.00351,-0.04566l-0.00176,0zm-20.91715,22.0638l2.16687,1.60145c-0.93418,0.91311 -1.81743,1.77353 -2.45485,2.38812l0.28798,-3.98957"
                  fill="white"
                ></path>
              </svg>
            </button>
            <button
              aria-label="whatsapp"
              className="react-share__ShareButton"
              onClick={shareEventHandlers.whatsappShare}
              style={{
                backgroundColor: "transparent",
                border: "none",
                padding: 0,
                font: "inherit",
                color: "inherit",
                cursor: "pointer",
              }}
            >
              <svg viewBox="0 0 64 64" width="32" height="32">
                <circle cx="32" cy="32" r="31" fill="#25D366"></circle>
                <path
                  d="m42.32286,33.93287c-0.5178,-0.2589 -3.04726,-1.49644 -3.52105,-1.66732c-0.4712,-0.17346 -0.81554,-0.2589 -1.15987,0.2589c-0.34175,0.51004 -1.33075,1.66474 -1.63108,2.00648c-0.30033,0.33658 -0.60066,0.36247 -1.11327,0.12191c-0.5178,-0.2589 -2.17994,-0.80259 -4.14759,-2.56312c-1.53269,-1.37217 -2.56312,-3.05503 -2.86603,-3.57283c-0.30033,-0.5178 -0.03366,-0.80259 0.22524,-1.06149c0.23301,-0.23301 0.5178,-0.59547 0.7767,-0.90616c0.25372,-0.31068 0.33657,-0.5178 0.51262,-0.85437c0.17088,-0.36246 0.08544,-0.64725 -0.04402,-0.90615c-0.12945,-0.2589 -1.15987,-2.83316 -1.58904,-3.86832c-0.41424,-1.00342 -0.84142,-0.88027 -1.15987,-0.88027c-0.29773,-0.02588 -0.64208,-0.02588 -0.98382,-0.02588c-0.34693,0 -0.90616,0.12944 -1.37736,0.62136c-0.4712,0.5178 -1.80194,1.76053 -1.80194,4.27186c0,2.51134 1.84596,4.945 2.10227,5.30747c0.2589,0.33657 3.63497,5.51458 8.80262,7.74113c1.23237,0.5178 2.1903,0.82848 2.94111,1.08738c1.23237,0.38836 2.35599,0.33657 3.24402,0.20712c0.99159,-0.15534 3.04985,-1.24272 3.47963,-2.45956c0.44013,-1.21683 0.44013,-2.22654 0.31068,-2.45955c-0.12945,-0.23301 -0.46601,-0.36247 -0.98382,-0.59548m-9.40068,12.84407l-0.02589,0c-3.05503,0 -6.08417,-0.82849 -8.72495,-2.38189l-0.62136,-0.37023l-6.47252,1.68286l1.73463,-6.29129l-0.41424,-0.64725c-1.70875,-2.71846 -2.6149,-5.85116 -2.6149,-9.07706c0,-9.39809 7.68934,-17.06155 17.15993,-17.06155c4.58253,0 8.88029,1.78265 12.11655,5.02718c3.23625,3.21937 5.02159,7.50812 5.02159,12.06281c-0.0078,9.3981 -7.69712,17.06155 -17.14699,17.06155m14.58906,-31.58846c-3.93529,-3.80743 -9.1133,-5.95648 -14.62789,-5.95648c-11.36055,0 -20.60848,9.2065 -20.61625,20.52564c0,3.61684 0.94757,7.14565 2.75211,10.26282l-2.92557,10.63564l10.93337,-2.85309c3.0136,1.63108 6.4052,2.4958 9.85634,2.49839l0.01037,0c11.36574,0 20.61884,-9.2091 20.62403,-20.53082c0,-5.48093 -2.14111,-10.64081 -6.03239,-14.51915"
                  fill="white"
                ></path>
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlayerV4;
